import React, { useState } from 'react';
import { getInvoiceHtml, downloadBillPDF, downloadBillImage, printBill } from '../../utils/billPdf';

export default function InvoicePreviewModal({ bill, patient, isOpen, onClose }) {
    const [copyType, setCopyType] = useState('digital'); // 'digital' or 'signature'

    if (!isOpen || !bill) return null;

    const patientObj = bill.patient || patient || {};
    const invoiceHtml = getInvoiceHtml(bill, patientObj, copyType);

    const handleBackdropClick = (e) => {
        if (e.target.id === 'invoice-modal-backdrop') {
            onClose();
        }
    };

    const getStatusBadge = (status) => {
        const s = (status || 'Paid').toLowerCase();
        if (s.includes('paid') && !s.includes('partially') && !s.includes('unpaid')) {
            return { bg: 'rgba(16,185,129,0.12)', color: '#065f46', border: '1px solid rgba(16,185,129,0.3)', label: 'PAID' };
        }
        if (s.includes('partially')) {
            return { bg: 'rgba(245,158,11,0.12)', color: '#b45309', border: '1px solid rgba(245,158,11,0.3)', label: 'PARTIALLY PAID' };
        }
        if (s.includes('cancelled')) {
            return { bg: 'rgba(239,68,68,0.12)', color: '#991b1b', border: '1px solid rgba(239,68,68,0.3)', label: 'CANCELLED' };
        }
        return { bg: 'rgba(100,116,139,0.12)', color: '#475569', border: '1px solid rgba(100,116,139,0.25)', label: 'UNPAID' };
    };

    const badge = getStatusBadge(bill.status);

    return (
        <div
            id="invoice-modal-backdrop"
            onClick={handleBackdropClick}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(15, 23, 42, 0.65)',
                backdropFilter: 'blur(3px)',
                zIndex: 1050,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '16px'
            }}
        >
            <div
                className="bg-white rounded-3 shadow-lg d-flex flex-column"
                style={{
                    width: '900px',
                    maxWidth: '96vw',
                    maxHeight: '94vh',
                    overflow: 'hidden',
                    border: '1px solid #e2e8f0'
                }}
            >
                {/* Modal Top Action Bar */}
                <div
                    className="px-4 py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2"
                    style={{ backgroundColor: '#f8fafc' }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold fs-5 text-dark">
                            Invoice #{bill.invoiceNumber || 'Preview'}
                        </span>
                        <span
                            className="badge fw-semibold"
                            style={{
                                backgroundColor: badge.bg,
                                color: badge.color,
                                border: badge.border,
                                fontSize: '0.75rem',
                                padding: '4px 8px'
                            }}
                        >
                            {badge.label}
                        </span>
                    </div>

                    {/* Copy Type Toggle & Action Buttons */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        {/* Copy Type Toggle */}
                        <div className="btn-group btn-group-sm me-1" role="group">
                            <button
                                type="button"
                                className={`btn ${copyType === 'digital' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setCopyType('digital')}
                                style={{ fontSize: '0.8rem', fontWeight: 500 }}
                            >
                                <i className="fa fa-desktop me-1"></i> Digital
                            </button>
                            <button
                                type="button"
                                className={`btn ${copyType === 'signature' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                onClick={() => setCopyType('signature')}
                                style={{ fontSize: '0.8rem', fontWeight: 500 }}
                            >
                                <i className="fa fa-signature me-1"></i> For Signature
                            </button>
                        </div>

                        {/* Print Button (Print without download) */}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-dark"
                            onClick={() => printBill(bill, patientObj, copyType)}
                            title="Print invoice directly"
                        >
                            <i className="fa fa-print me-1"></i> Print
                        </button>

                        {/* Download PDF Button */}
                        <button
                            type="button"
                            className="btn btn-sm btn-success text-white"
                            onClick={() => downloadBillPDF(bill, patientObj, copyType)}
                            title="Download invoice as PDF"
                            style={{ backgroundColor: 'var(--primary, #0d9488)', borderColor: 'var(--primary, #0d9488)' }}
                        >
                            <i className="fa fa-download me-1"></i> Download PDF
                        </button>

                        {/* Download Image Button */}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => downloadBillImage(bill, patientObj, copyType)}
                            title="Download invoice as Image (PNG)"
                        >
                            <i className="fa fa-image me-1"></i> Image
                        </button>

                        {/* Open in Tab Button */}
                        {bill._id && (
                            <a
                                href={`/invoice/${bill._id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                                title="Open full view in new tab"
                            >
                                <i className="fa fa-external-link-alt"></i>
                            </a>
                        )}

                        {/* Close Button */}
                        <button
                            type="button"
                            className="btn-close ms-2"
                            aria-label="Close"
                            onClick={onClose}
                        ></button>
                    </div>
                </div>

                {/* Modal Scrollable Body showing Invoice */}
                <div
                    className="p-3 p-md-4 overflow-auto flex-grow-1"
                    style={{
                        backgroundColor: '#64748b',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'flex-start'
                    }}
                >
                    <div
                        className="shadow-lg bg-white rounded-1"
                        style={{
                            width: '794px',
                            maxWidth: '100%',
                            boxSizing: 'border-box',
                            overflow: 'auto'
                        }}
                        dangerouslySetInnerHTML={{ __html: invoiceHtml }}
                    />
                </div>
            </div>
        </div>
    );
}

